/**
 * useLeadSource — Capture & persist UTM params, referrer, and affiliate codes.
 *
 * Call `captureLeadSource()` on the landing/login page (once, on mount).
 * Call `saveLeadSource(userId)` after authentication to write to DB.
 *
 * First-touch attribution: params are captured once and never overwritten in localStorage.
 * This ensures the original source is preserved even if the user visits multiple times.
 *
 * Every first visit is captured — including direct/organic traffic with no UTM —
 * and classified into source/medium (google/organic, direct/none, …) so the
 * admin analytics no longer collapse non-campaign traffic into "Unknown".
 */

import { supabase } from "@/integrations/supabase/client";
import { classifyTraffic } from "@/lib/trafficSource";
import { getLeadAttribution } from "@/lib/attribution";

const LS_KEY = "te_lead_source";
const GEO_KEY = "te_geo";

export interface LeadSourceData {
  utm_source:   string | null;
  utm_medium:   string | null;
  utm_campaign: string | null;
  utm_term:     string | null;
  utm_content:  string | null;
  referrer_url: string | null;
  landing_page: string | null;
  affcode:      string | null;
  partner_code: string | null;
  ref_code:     string | null;
  raw_params:   Record<string, string>;
  first_seen_at: string;
  /** Classified at capture time (see src/lib/trafficSource.ts). */
  source?:      string | null;
  medium?:      string | null;
  /** Connect-from geo, resolved by the edge worker (/api/geo). */
  country?:     string | null;
  city?:        string | null;
  region?:      string | null;
}

export interface GeoData {
  country: string | null;
  city:    string | null;
  region:  string | null;
}

/**
 * Resolve the visitor's connect-from geo via the Cloudflare edge worker.
 * Best-effort: returns null on dev servers (no worker) or network failure.
 * Cached in sessionStorage so it's fetched at most once per tab.
 */
export async function getVisitorGeo(): Promise<GeoData | null> {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) return JSON.parse(cached) as GeoData;
  } catch { /* storage may be blocked */ }

  try {
    const res = await fetch("/api/geo", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const geo = (await res.json()) as GeoData;
    try { sessionStorage.setItem(GEO_KEY, JSON.stringify(geo)); } catch { /* ignore */ }
    return geo;
  } catch {
    return null;
  }
}

/** Read URL params + referrer and store in localStorage (first-touch only). */
export function captureLeadSource(): LeadSourceData | null {
  try {
    // Don't overwrite existing first-touch data
    const existing = localStorage.getItem(LS_KEY);
    if (existing) {
      return JSON.parse(existing) as LeadSourceData;
    }

    const params  = new URLSearchParams(window.location.search);
    const rawParams: Record<string, string> = {};
    params.forEach((v, k) => { rawParams[k] = v; });

    const classified = classifyTraffic({
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      referrer_url: document.referrer || null,
      click_ids: rawParams,
    });

    const data: LeadSourceData = {
      utm_source:   params.get("utm_source"),
      utm_medium:   params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term:     params.get("utm_term"),
      utm_content:  params.get("utm_content"),
      referrer_url: document.referrer || null,
      landing_page: window.location.pathname + window.location.search,
      affcode:      params.get("affcode"),
      partner_code: params.get("partner") ?? params.get("partner_code"),
      ref_code:     params.get("refcode") ?? params.get("ref"),
      raw_params:   rawParams,
      first_seen_at: new Date().toISOString(),
      source:       classified.source,
      medium:       classified.medium,
    };

    localStorage.setItem(LS_KEY, JSON.stringify(data));

    // Enrich with connect-from geo asynchronously (never blocks capture).
    void getVisitorGeo().then((geo) => {
      if (!geo?.country) return;
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return;
        const stored = JSON.parse(raw) as LeadSourceData;
        if (stored.country) return;
        localStorage.setItem(LS_KEY, JSON.stringify({ ...stored, ...geo }));
      } catch { /* ignore */ }
    });

    return data;
  } catch {
    return null;
  }
}

/** Retrieve stored lead source (if any). */
export function getStoredLeadSource(): LeadSourceData | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as LeadSourceData) : null;
  } catch {
    return null;
  }
}

/** Save lead source to Supabase for an authenticated user. Idempotent (upsert). */
export async function saveLeadSource(userId: string): Promise<void> {
  const data = getStoredLeadSource();
  if (!data) return;

  // Last paid click (90d) + Meta cookies ride along in raw_params so the
  // application can be uploaded to Google Ads / Meta when it is submitted,
  // even if the first touch was organic or direct.
  let lastClick: Record<string, unknown> = {};
  try {
    const a = getLeadAttribution();
    lastClick = {
      _last_click: a.attribution.last_click,
      _click_ids: Object.fromEntries(
        Object.entries({ gclid: a.gclid, gbraid: a.gbraid, wbraid: a.wbraid, fbclid: a.fbclid, msclkid: a.msclkid, ttclid: a.ttclid, fbc: a.fbc, fbp: a.fbp })
          .filter(([, v]) => v),
      ),
    };
  } catch { /* attribution is best-effort */ }

  try {
    await (supabase.from("lead_sources" as any) as any).upsert(
      {
        user_id:      userId,
        utm_source:   data.utm_source,
        utm_medium:   data.utm_medium,
        utm_campaign: data.utm_campaign,
        utm_term:     data.utm_term,
        utm_content:  data.utm_content,
        referrer_url: data.referrer_url,
        landing_page: data.landing_page,
        affcode:      data.affcode,
        partner_code: data.partner_code,
        ref_code:     data.ref_code,
        raw_params:   { ...data.raw_params, ...lastClick },
        first_seen_at: data.first_seen_at,
        converted_at:  new Date().toISOString(),
        source:       data.source ?? null,
        medium:       data.medium ?? null,
        country:      data.country ?? null,
        city:         data.city ?? null,
      },
      { onConflict: "user_id", ignoreDuplicates: false }
    );
  } catch (e) {
    console.warn("[LeadSource] Failed to save:", e);
  }
}

/** Clear stored lead source (call after saving, or on explicit logout). */
export function clearLeadSource(): void {
  localStorage.removeItem(LS_KEY);
}
